import { CheckIcon, CodeBracketIcon, CreditCardIcon, GlobeAltIcon, MicrophoneIcon, MoonIcon, MusicalNoteIcon, PauseIcon, PhoneIcon, PlayIcon, ScaleIcon, ShieldCheckIcon, SunIcon, UserGroupIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { ColoredCrispyVoiceLogoWithText } from '#common/components/ColoredCrispyVoiceLogoWithText.tsx';
import LanguagePicker from '#common/components/LanguagePicker.tsx';
import { VideoPlayer } from '#common/components/VideoPlayer.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { LinkTypeIcon, type LinkType } from '#common/utils/link-types.tsx';
import { useCoreTheme } from '#speaker-shop/hooks/useCoreTheme';
import { resolveSelectedUseId } from '#speaker-shop/models/unified-wizard';
import { getWebsiteUrl } from '#speaker-shop/utils/public-env.tsx';
import { AudioProvider, useAudio } from './AudioContext';
import { ContactFormDialog } from './ContactFormDialog';
import { CoreOrderFlow } from './CoreOrderFlow';
import { NaturalVoiceBookingDialog } from './NaturalVoiceBookingDialog';
import { RecordingProvider } from './RecordingContext';
import { type UnifiedShopData } from './shop-layouts/types';
import { StarRating } from './StarRating';

interface Props {
    data: UnifiedShopData;
    baseUrl: string;
}

const trustIcons = [ ShieldCheckIcon, ScaleIcon, CreditCardIcon, GlobeAltIcon ];
const uspIcons = [ ShieldCheckIcon, ScaleIcon, MusicalNoteIcon, UserGroupIcon ];

function barHeights(count: number): number[] {
    return Array.from({ length: count }, (_, index) => 6 + Math.round(14 * Math.abs(Math.sin(index * 1.7))));
}

function WaveBars({ count }: { count: number }) {
    const heights = useMemo(() => barHeights(count), [ count ]);
    return (
        <div className='flex h-7 min-w-0 flex-1 items-center gap-0.5 overflow-hidden'>
            {heights.map((height, index) => (
                <span
                    className='min-h-1 flex-1 rounded-[1px] bg-current opacity-45'
                    key={index}
                    style={{ height: `${height}px` }}
                />
            ))}
        </div>
    );
}

function ComparePlayButton({ src, variant }: { src: string | null; variant: 'natural' | 'clone' }) {
    const { isPlayingAudio, playAudio, stopAudio } = useAudio();
    const [ playingSrc, setPlayingSrc ] = useState<string | null>(null);
    const isThisPlaying = isPlayingAudio && !!src && playingSrc === src;

    const onClick = useCallback(() => {
        if (!src) {
            return;
        }
        if (isThisPlaying) {
            stopAudio();
            setPlayingSrc(null);
            return;
        }
        playAudio(src);
        setPlayingSrc(src);
    }, [ isThisPlaying, playAudio, src, stopAudio ]);

    return (
        <button
            type='button'
            onClick={onClick}
            disabled={!src}
            className={clsx(
                'flex size-7 shrink-0 items-center justify-center rounded-full transition hover:scale-105 disabled:opacity-30',
                variant === 'natural' ? 'bg-[var(--core-yellow)] text-[#0d2233]' : 'bg-[var(--core-blue)] text-white'
            )}
            aria-label={variant}
        >
            {isThisPlaying ? <PauseIcon className='size-3.5' /> : <PlayIcon className='size-3.5' />}
        </button>
    );
}

function ComparePlayer({ naturalSrc, cloneSrc }: { naturalSrc: string | null; cloneSrc: string | null }) {
    const { t } = useEditableTranslation('core-shop');
    if (!naturalSrc && !cloneSrc) {
        return null;
    }

    return (
        <div className='core-compare-player'>
            <div className='mb-1.5 flex justify-between text-[10px] font-bold uppercase tracking-[0.5px] opacity-45'>
                <span>{t('hero.compare-natural')}</span>
                <span>{t('hero.compare-clone')}</span>
            </div>
            <div className='flex h-10 overflow-hidden rounded-full'>
                <div className='flex min-w-0 flex-1 items-center gap-2 bg-[var(--core-soft-bg)] px-2 text-[var(--core-text)]'>
                    <ComparePlayButton src={naturalSrc} variant='natural' />
                    <WaveBars count={13} />
                </div>
                <div className='flex min-w-0 flex-1 items-center gap-2 bg-[color-mix(in_srgb,var(--core-blue)_28%,transparent)] px-2 text-[var(--core-blue)]'>
                    <WaveBars count={13} />
                    <ComparePlayButton src={cloneSrc} variant='clone' />
                </div>
            </div>
        </div>
    );
}

export function CoreUnifiedShop({ data, baseUrl }: Props) {
    const { t, rawT } = useEditableTranslation('core-shop');
    const { t: tHero } = useEditableTranslation('hero');
    const { theme, toggleTheme } = useCoreTheme();
    const translatePath = useTranslatedPath(useLocale());
    const [ activeVoiceId, setActiveVoiceId ] = useState<string>(data.voices[0]?.id ?? '');
    const [ selectedUseId, setSelectedUseId ] = useState<string | null>(() =>
        resolveSelectedUseId(null, data.availableUses)
    );
    const [ showContactForm, setShowContactForm ] = useState(false);
    const [ showApiContactForm, setShowApiContactForm ] = useState(false);
    const [ showNaturalVoiceBooking, setShowNaturalVoiceBooking ] = useState(false);
    const [ highlightTestimonials, setHighlightTestimonials ] = useState(false);
    const activeVoice = data.voices.find(voice => voice.id === activeVoiceId) ?? data.voices[0];
    const hasAnyVoiceDemos = data.voices.some(voice => voice.naturalHlsSrc || voice.cloneHlsSrc);
    const minimumPrice = Math.min(...data.availableUses.map(use => use.basePrice));
    const averageRating = useMemo(
        () =>
            data.testimonials.length === 0
                ? 0
                : data.testimonials.reduce((sum, testimonial) => sum + testimonial.rating, 0)
                    / data.testimonials.length,
        [ data.testimonials ]
    );

    useEffect(() => {
        setSelectedUseId(current => resolveSelectedUseId(current, data.availableUses));
    }, [ data.availableUses ]);

    useEffect(() => {
        if (!highlightTestimonials) {
            return;
        }
        const timer = setTimeout(() => setHighlightTestimonials(false), 2000);
        return () => clearTimeout(timer);
    }, [ highlightTestimonials ]);

    if (!activeVoice) {
        return null;
    }

    const scrollToOrder = () => {
        document.getElementById('cvx-order-flow')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const scrollToTestimonials = () => {
        document.getElementById('cvx-testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightTestimonials(true);
    };

    return (
        <AudioProvider>
            <div className='core-unified' data-theme={theme}>
                <nav className='core-nav'>
                    <a className='core-logo' href='#top' aria-label='CrispyVoice'>
                        <ColoredCrispyVoiceLogoWithText className='core-logo-mark' />
                    </a>
                    <div className='core-nav-trust'>
                        {([ 'voice-owner-approved', 'ai-act-ready', 'card-payment', 'languages' ] as const).map(
                            (key, index) => {
                                const Icon = trustIcons[index]!;
                                return (
                                    <span className='core-trust-pill' key={key}>
                                        <Icon className='size-3' /> {t(`topbar.${key}`)}
                                    </span>
                                );
                            }
                        )}
                    </div>
                    <div className='core-nav-actions'>
                        <button
                            type='button'
                            className='core-icon-button'
                            onClick={toggleTheme}
                            aria-label={rawT('nav.theme')}
                        >
                            <SunIcon className='core-theme-icon-light size-4' />
                            <MoonIcon className='core-theme-icon-dark size-4' />
                        </button>
                        <LanguagePicker baseUrl={baseUrl} className='core-language-picker' />
                        <button
                            type='button'
                            className='core-nav-button core-nav-button-outline'
                            onClick={() => setShowApiContactForm(true)}
                        >
                            <CodeBracketIcon className='core-nav-button-icon size-4' />
                            <span>{t('nav.api')}</span>
                        </button>
                        <button type='button' className='core-nav-button' onClick={() => setShowContactForm(true)}>
                            <PhoneIcon className='core-nav-button-icon size-4' />
                            <span>{t('nav.contact')}</span>
                        </button>
                    </div>
                </nav>

                <main id='top'>
                    <section className='core-page core-hero'>
                        <h1 className='core-hero-heading'>
                            {t('hero.headline-main')}
                            <br />
                            <span>{t('hero.headline-accent')}</span>
                        </h1>

                        <div className='core-portrait-frame'>
                            <div className={clsx('core-portrait', data.portraitVideoHlsSrc && 'core-portrait-video')}>
                                {data.portraitVideoHlsSrc
                                    ? (
                                        <VideoPlayer
                                            hlsSrc={data.portraitVideoHlsSrc}
                                            hlsAssetPath={data.portraitVideoHlsAssetPath}
                                            posterSrc={data.portraitImageSrc ?? ''}
                                            className='core-portrait-video-player'
                                            videoClassName='core-portrait-video-media'
                                            autoPlay
                                        />
                                    )
                                    : data.portraitImageSrc
                                    ? (
                                        <img
                                            src={data.portraitImageSrc}
                                            alt={data.speakerDisplayName ?? ''}
                                            className='size-full object-cover'
                                        />
                                    )
                                    : <MicrophoneIcon className='size-16 text-white/45' />}
                            </div>
                        </div>

                        <div className='pt-1'>
                            {data.testimonials.length > 0 && (
                                <button type='button' className='core-rating' onClick={scrollToTestimonials}>
                                    <StarRating
                                        rating={averageRating}
                                        size='small'
                                        colors={{
                                            filled: 'fill-[var(--core-accent)]',
                                            empty: 'fill-[var(--core-soft-bg)]'
                                        }}
                                    />
                                    <strong>{averageRating.toFixed(1)}</strong>
                                    <span>{data.testimonials.length} {tHero('reviews')}</span>
                                </button>
                            )}
                            <h2 className='mb-2 text-2xl font-bold'>{data.speakerDisplayName}</h2>
                            {data.bio && <p className='mb-6 text-sm leading-6 opacity-55'>{data.bio}</p>}

                            <div className='mb-6'>
                                <h3 className='mb-3 text-sm font-bold text-[var(--core-blue)]'>
                                    {t('hero.why-title')}
                                </h3>
                                <ul className='space-y-2'>
                                    {([ 'why-1', 'why-2', 'why-3', 'why-4' ] as const).map(key => (
                                        <li className='flex items-start gap-2 text-[13px] opacity-75' key={key}>
                                            <CheckIcon className='mt-0.5 size-4 shrink-0 text-[var(--core-blue)]' />
                                            {t(`hero.${key}`)}
                                        </li>
                                    ))}
                                </ul>
                                <p className='mt-3 border-t border-[var(--core-muted-border)] pt-3 text-xs opacity-45'>
                                    {t('hero.caption', {
                                        currency: data.currency,
                                        price: Number.isFinite(minimumPrice) ? minimumPrice.toFixed(2) : '0.00'
                                    })}
                                </p>
                            </div>

                            {data.voices.length > 1 && hasAnyVoiceDemos && (
                                <div className='mb-4 flex flex-wrap gap-2'>
                                    {data.voices.map(voice => (
                                        <button
                                            type='button'
                                            className={clsx('core-voice-tab', voice.id === activeVoiceId && 'active')}
                                            key={voice.id}
                                            onClick={() => setActiveVoiceId(voice.id)}
                                        >
                                            {voice.name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {activeVoice.naturalHlsSrc || activeVoice.cloneHlsSrc
                                ? (
                                    <ComparePlayer
                                        naturalSrc={activeVoice.naturalHlsSrc}
                                        cloneSrc={activeVoice.cloneHlsSrc}
                                    />
                                )
                                : hasAnyVoiceDemos && (
                                    <p className='core-card px-4 py-3 text-sm opacity-65'>{t('hero.no-demos')}</p>
                                )}
                            <div className='mt-5 flex flex-wrap items-center gap-4'>
                                <button type='button' className='core-primary-button' onClick={scrollToOrder}>
                                    {t('hero.cta-clone')} <PlayIcon className='size-4' />
                                </button>
                                <button
                                    type='button'
                                    className='core-text-button'
                                    onClick={() => setShowNaturalVoiceBooking(true)}
                                >
                                    <PhoneIcon className='size-4' /> {t('hero.cta-natural')}
                                </button>
                            </div>
                        </div>
                    </section>

                    <RecordingProvider storageKey={`unified:${data.speakerId}`}>
                        <CoreOrderFlow
                            data={data}
                            activeVoice={activeVoice}
                            activeVoiceId={activeVoiceId}
                            selectedUseId={selectedUseId}
                            onActiveVoiceChange={setActiveVoiceId}
                            onSelectedUseChange={setSelectedUseId}
                            onApiRequest={() => setShowApiContactForm(true)}
                            theme={theme}
                        />
                    </RecordingProvider>

                    <section className='core-page core-content-section'>
                        <div className='core-section-label'>{t('usp.label')}</div>
                        <div className='grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 lg:grid-cols-4'>
                            {([ '1', '2', '3', '4' ] as const).map((number, index) => {
                                const Icon = uspIcons[index]!;
                                return (
                                    <article className='core-card core-lift-card p-[18px]' key={number}>
                                        <div className='mb-3 flex size-9 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--core-blue)_15%,transparent)] text-[var(--core-blue)]'>
                                            <Icon className='size-4' />
                                        </div>
                                        <h3 className='mb-1.5 text-sm font-bold'>{t(`usp.${number}-title`)}</h3>
                                        <p className='text-xs leading-5 opacity-50'>{t(`usp.${number}-text`)}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </section>

                    {data.testimonials.length > 0 && (
                        <section className='core-page core-content-section' id='cvx-testimonials'>
                            <div
                                className={clsx(
                                    'rounded-xl transition-all duration-700',
                                    highlightTestimonials
                                        && 'ring-2 ring-[var(--core-accent)] ring-offset-4 ring-offset-[var(--core-bg)]'
                                )}
                            >
                                <div className='core-section-label'>{t('testimonials.label')}</div>
                                <div className='grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3'>
                                    {data.testimonials.map(testimonial => (
                                        <article className='core-card core-lift-card p-[18px]' key={testimonial.id}>
                                            <div className='mb-2.5'>
                                                <StarRating
                                                    rating={testimonial.rating}
                                                    size='small'
                                                    colors={{
                                                        filled: 'fill-[var(--core-accent)]',
                                                        empty: 'fill-[var(--core-soft-bg)]'
                                                    }}
                                                />
                                            </div>
                                            <p className='mb-3 text-[13px] italic leading-6 opacity-70'>
                                                “{testimonial.text}”
                                            </p>
                                            <div className='text-[11px] font-bold uppercase tracking-[0.5px] opacity-40'>
                                                {testimonial.name}
                                                {testimonial.company ? ` · ${testimonial.company}` : ''}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}

                    <section className='core-cta'>
                        <h2 className='mb-2 text-[26px] font-bold'>{t('cta.title')}</h2>
                        <p className='mb-6 text-[15px] opacity-55'>{t('cta.subtitle')}</p>
                        <div className='flex flex-wrap justify-center gap-3'>
                            <button type='button' className='core-primary-button' onClick={scrollToOrder}>
                                {t('cta.primary')}
                            </button>
                            <button
                                type='button'
                                className='core-secondary-button'
                                onClick={() => setShowNaturalVoiceBooking(true)}
                            >
                                {t('cta.secondary')}
                            </button>
                        </div>
                    </section>
                </main>

                <footer className='core-footer'>
                    <span className='core-footer-left'>{t('footer.left')}</span>
                    <div className='core-footer-links flex flex-wrap items-center justify-center gap-x-4 gap-y-2'>
                        {data.links.length > 0 && (
                            <div className='core-footer-social flex items-center gap-3'>
                                {data.links.map(link => (
                                    <a
                                        className='transition hover:scale-110 active:scale-95'
                                        key={link.id}
                                        href={link.url}
                                        target='_blank'
                                        rel='noreferrer'
                                        aria-label={link.type}
                                    >
                                        <LinkTypeIcon type={link.type as LinkType} className='size-4' />
                                    </a>
                                ))}
                            </div>
                        )}
                        <div className='core-footer-legal flex items-center gap-4'>
                            <a className='core-footer-link' href={translatePath('/gtc')}>{t('footer:gtc')}</a>
                            <a className='core-footer-link' href={translatePath('/imprint')}>{t('footer:imprint')}</a>
                        </div>
                    </div>
                    <span className='core-footer-right text-[10px] font-semibold tracking-[0.5px] text-[#ffd700]/45'>
                        Powered by <a href={getWebsiteUrl()} target='_blank' rel='noreferrer'>CrispyVoice</a>
                    </span>
                </footer>

                <ContactFormDialog
                    topic='Contact real voice'
                    description={<p>{t('common:contact-form-description')}</p>}
                    open={showContactForm}
                    onClose={() => setShowContactForm(false)}
                    appearance='unified'
                    theme={theme}
                />
                <ContactFormDialog
                    topic='API'
                    description={
                        <div>
                            <p>{t('common:api-contact-form-description')}</p>
                            <p>{t('common:api-contact-form-instruction')}</p>
                        </div>
                    }
                    open={showApiContactForm}
                    onClose={() => setShowApiContactForm(false)}
                    appearance='unified'
                    theme={theme}
                />
                <NaturalVoiceBookingDialog
                    speakerName={data.speakerDisplayName ?? ''}
                    theme={theme}
                    open={showNaturalVoiceBooking}
                    onClose={() => setShowNaturalVoiceBooking(false)}
                />
            </div>
        </AudioProvider>
    );
}
